import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { BoldfitMark } from '@/components/boldfit-mark'
import { useAuth } from '@/lib/auth'
import { useDensity } from '@/hooks/use-density'
import { useTheme } from '@/hooks/use-theme'

export function Shell({ children }: { children: ReactNode }) {
  const { theme, cycleTheme } = useTheme({ storageKey: 'herald-theme' })
  const { density, toggleDensity } = useDensity({ storageKey: 'herald-density' })
  const { account, signOut, authenticated } = useAuth()
  const themeLabel = { system: 'System', light: 'Light', dark: 'Dark' }[theme]
  const densityLabel = density === 'compact' ? 'Compact' : 'Comfortable'

  return (
    <div className="flex h-dvh overflow-hidden bg-background text-ink">
      <aside className="flex w-[196px] shrink-0 flex-col bg-rail">
        <div className="flex items-center gap-2 px-4 pt-4 pb-5 text-rail-text-strong">
          <BoldfitMark className="h-[18px] w-auto" />
          <span className="font-display text-meta font-extrabold tracking-[0.28em]">HERALD</span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2.5">
          <NavLink
            to="/messages"
            className={({ isActive }) =>
              `rounded-md px-[11px] py-2 text-body font-semibold ${
                isActive
                  ? 'bg-rail-sel text-rail-text-strong'
                  : 'text-rail-text hover:text-rail-text-strong'
              }`
            }
          >
            Messages
          </NavLink>
        </nav>
        <div className="mt-auto flex flex-col pb-3">
          <button
            type="button"
            onClick={cycleTheme}
            title="Switch between light, dark and system"
            className="mx-2.5 rounded-md px-[11px] py-2 text-left text-body font-semibold text-rail-text hover:text-rail-text-strong"
          >
            Theme · {themeLabel}
          </button>
          <button
            type="button"
            onClick={toggleDensity}
            title="Switch row density"
            className="mx-2.5 rounded-md px-[11px] py-2 text-left text-body font-semibold text-rail-text hover:text-rail-text-strong"
          >
            Density · {densityLabel}
          </button>
          {authenticated && account ? (
            <button
              type="button"
              onClick={() => void signOut()}
              className="mx-2.5 rounded-md px-[11px] py-2 text-left text-body font-semibold text-rail-text hover:text-rail-text-strong"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </aside>
      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">{children}</main>
    </div>
  )
}
